import { FC, useMemo, useState } from 'react'

import {
  TabItems,
  type TabItemsOptionValue,
  TabItemsSizes,
  TabItemsVariants
} from '@inno-ui/tab-items'
import { useTranslation } from 'react-i18next'
import { useSetActiveWallpaperMutation } from 'shared/api/containerApi/requests/setActiveWallpaper'
import { useDesktopUtilityWindowsStore } from 'shared/stores/desktopUtilityWindowsStore'
import { useWallpaperStore } from 'shared/stores/wallpaperStore'
import { WallpaperDataType } from 'shared/types/index'
import { isTesting } from 'shared/utils/isTesting'
import { useAnimatedWallpaperCatalog } from 'shared/wallpapers/catalog'
import { resolveAnimatedWallpaperScene } from 'shared/wallpapers/resolveAnimatedWallpaperScene'

import { UtilityWindowWrapper } from '../UtilityWindowWrapper'
import { WallpaperCategory } from './WallpapersSelector.enums'
import styles from './WallpapersSelector.module.scss'
import { UploadWallpaper, WallpaperItem } from './components'
import { SeasonPreview } from './components/SeasonPreview'

export const WallpapersSelector: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.wallpaper'
  })
  const setCurrentWallpaper = useWallpaperStore(
    state => state.setCurrentWallpaper
  )
  const currentWallpaper = useWallpaperStore(state => state.currentWallpaper)
  const predefinedWallpapers = useWallpaperStore(
    state => state.predefinedWallpapers
  )
  const customWallpapers = useWallpaperStore(state => state.customWallpapers)
  const animatedDefinitions = useAnimatedWallpaperCatalog(
    state => state.wallpapers
  )
  const activeDefinition = animatedDefinitions.find(
    item => item.id === currentWallpaper.id
  )
  const [selectedCategory, setSelectedCategory] = useState<WallpaperCategory>(
    WallpaperCategory.All
  )
  const isWallpaperSelectionWindowOpen = useDesktopUtilityWindowsStore(
    state => state.activeWindow === 'wallpaper'
  )
  const closeWindow = useDesktopUtilityWindowsStore(state => state.closeWindow)

  const setActiveWallpaperMutation = useSetActiveWallpaperMutation()

  const handleClose = () => {
    closeWindow()
  }

  const handleChangeWallpaper = (
    wallpaper: WallpaperDataType,
    isCustom: boolean = false
  ) => {
    const previous = currentWallpaper
    setCurrentWallpaper(wallpaper)
    setActiveWallpaperMutation.mutate(
      {
        wallpaper_type: isCustom ? 'custom' : 'predefined',
        wallpaper_id: String(wallpaper.id)
      },
      {
        onError: () => {
          if (useWallpaperStore.getState().currentWallpaper.id === wallpaper.id)
            setCurrentWallpaper(previous)
        }
      }
    )
  }

  const handleCategoryChange = (value: TabItemsOptionValue) => {
    if (
      value === WallpaperCategory.All ||
      value === WallpaperCategory.System ||
      value === WallpaperCategory.Personal
    ) {
      setSelectedCategory(value)
    }
  }

  // Recompute on the next render after a date change as well as catalog updates.
  const calendarDate = new Date().toDateString()
  const systemWallpapers = useMemo(
    () => [
      ...predefinedWallpapers.filter(item => !item.animation),
      ...animatedDefinitions.flatMap(definition => {
        const wallpaper = predefinedWallpapers.find(
          item => item.id === definition.id
        )
        if (!wallpaper) return []
        const scene = resolveAnimatedWallpaperScene(
          definition,
          new Date(calendarDate)
        )
        return [
          {
            ...wallpaper,
            image: `${definition.assetBaseUrl}${scene.poster}`,
            thumbnail: `${definition.assetBaseUrl}${scene.thumbnail}`
          }
        ]
      })
    ],
    [predefinedWallpapers, animatedDefinitions, calendarDate]
  )
  const renderSystemWallpapers = () => (
    <section
      className={styles.wallpaperCategory}
      aria-labelledby='system-wallpapers-heading'
    >
      <h3 id='system-wallpapers-heading' className={styles.categoryTitle}>
        {t('system_wallpapers')}
      </h3>
      <div className={styles.wallpapersContainer}>
        {systemWallpapers.map(wallpaper => (
          <WallpaperItem
            key={wallpaper.id}
            wallpaper={wallpaper}
            isSelected={currentWallpaper.id === wallpaper.id}
            onClick={() => handleChangeWallpaper(wallpaper, false)}
            selectedText={t('selected_wallpaper')}
          />
        ))}
      </div>
    </section>
  )

  const renderPersonalWallpapers = () => (
    <section
      className={styles.wallpaperCategory}
      aria-labelledby='personal-wallpapers-heading'
    >
      <h3 id='personal-wallpapers-heading' className={styles.categoryTitle}>
        {t('personal_wallpapers')}
      </h3>
      <div className={styles.wallpapersContainer}>
        {customWallpapers.map(wallpaper => (
          <WallpaperItem
            key={wallpaper.id}
            wallpaper={wallpaper}
            isSelected={currentWallpaper.id === wallpaper.id}
            onClick={() => handleChangeWallpaper(wallpaper, true)}
            selectedText={t('selected_wallpaper')}
            isCustom={true}
          />
        ))}
        <UploadWallpaper />
      </div>
    </section>
  )

  return (
    <UtilityWindowWrapper
      isOpen={isWallpaperSelectionWindowOpen}
      title={t('title')}
      onClose={handleClose}
    >
      <div className={styles.main}>
        <div className={styles.wallpaperTabs}>
          <TabItems
            options={[
              { value: WallpaperCategory.All, label: t('tabs.all') },
              { value: WallpaperCategory.System, label: t('tabs.system') },
              {
                value: WallpaperCategory.Personal,
                label: t('tabs.personal')
              }
            ]}
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant={TabItemsVariants.Default}
            size={TabItemsSizes.Size34}
            noPadding={true}
            scrollable={false}
          />
        </div>
        {isTesting() && activeDefinition?.renderer === 'seasonal' && (
          <SeasonPreview />
        )}
        {selectedCategory === WallpaperCategory.All && (
          <section id='all-wallpapers-panel' className={styles.wallpaperPanel}>
            {renderSystemWallpapers()}
            {renderPersonalWallpapers()}
          </section>
        )}
        {selectedCategory === WallpaperCategory.System && (
          <section
            id='system-wallpapers-panel'
            className={styles.wallpaperPanel}
          >
            {renderSystemWallpapers()}
          </section>
        )}
        {selectedCategory === WallpaperCategory.Personal && (
          <section
            id='personal-wallpapers-panel'
            className={styles.wallpaperPanel}
          >
            {renderPersonalWallpapers()}
          </section>
        )}
      </div>
    </UtilityWindowWrapper>
  )
}
